import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, RotateCw, Calendar } from "lucide-react";
import { CompactTaskCard } from "@/components/CompactTaskCard";
import { todayStr, taskOccursOnIso, getOccurrencesOnIso, isTaskSnoozed, isOccurrenceSnoozed } from "@/utils";

const WEEKDAY_ABBR = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const fmtISO = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** WEEKLY view — 7-day strip starting from TODAY (matches the phone screensaver's This Week
 *  strip). Prev/next buttons shift the anchor by 7 days. Clicking a date scrolls to and
 *  highlights that day's section. */
export const WeeklyView = ({ tasks, attachments, onToggle, onEdit, searchTerm, onPostpone, onUndoPostpone }) => {
    const [anchor, setAnchor] = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const [selected, setSelected] = useState(todayStr());

    const days = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(anchor);
            d.setDate(anchor.getDate() + i);
            return d;
        });
    }, [anchor]);

    // Build `iso → tasks[]` by asking each task "do you occur on this day?" — this naturally
    // handles daily/weekly recurring items on ALL their occurrence days plus per-instance
    // overrides (postpone). Snoozed items and completed non-recurring items are excluded.
    const tasksByDate = useMemo(() => {
        const map = {};
        const now = Date.now();
        for (const day of days) {
            const iso = fmtISO(day);
            const bucket = [];
            for (const t of tasks || []) {
                if (t.archived) continue;
                if (isTaskSnoozed(t, now)) continue;
                if (t.completed && t.type !== "recurring") continue;
                if (t.type === "recurring" && t.interval) {
                    // one row per obligation on this day (native beat + any beats moved here)
                    for (const occ of getOccurrencesOnIso(t, iso)) {
                        if ((t.completions || []).includes(occ.key)) continue;
                        if (isOccurrenceSnoozed(t, occ.key, now)) continue;
                        bucket.push({ ...t, time: occ.time, _occKey: occ.key, _displayIso: occ.displayIso, _movedFrom: occ.movedFrom });
                    }
                    continue;
                }
                if (taskOccursOnIso(t, iso)) bucket.push(t);
            }
            if (bucket.length) map[iso] = bucket;
        }
        for (const k of Object.keys(map)) {
            map[k].sort((a, b) => {
                const aH = a.time ? 0 : 1, bH = b.time ? 0 : 1;
                if (aH !== bH) return aH - bH;
                return (a.time || "").localeCompare(b.time || "");
            });
        }
        return map;
    }, [tasks, days]);

    const monthLabel = anchor.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const today = todayStr();

    const selectDay = (iso) => setSelected(iso);

    // If the visible week changes and the previously-selected day is no longer in it,
    // move selection to the first day of the new window.
    React.useEffect(() => {
        const weekIsos = days.map(fmtISO);
        if (!weekIsos.includes(selected)) setSelected(weekIsos[0]);
    }, [days, selected]);

    const selectedTasks = tasksByDate[selected] || [];
    const selectedLabel = (() => {
        const [yy, mm, dd] = selected.split("-").map(Number);
        const d = new Date(yy, mm - 1, dd);
        return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
    })();

    return (
        <div className="fade-up" data-testid="weekly-view">
            <div className="rounded-xl border border-meye-border bg-meye-card/60 backdrop-blur-sm p-4 mt-2">
                <div className="flex items-center justify-between mb-2">
                    <button
                        onClick={() => setAnchor((a) => { const n = new Date(a); n.setDate(a.getDate() - 7); return n; })}
                        className="w-8 h-8 rounded-lg border border-meye-border bg-black/40 text-zinc-400 hover:text-white flex items-center justify-center"
                        data-testid="weekly-prev"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <div className="text-center">
                        <h3 className="font-brand text-lg font-extrabold uppercase bg-gradient-to-r from-meye-fuchsia to-violet-400 bg-clip-text text-transparent tracking-wide">
                            Next 7 days · {monthLabel}
                        </h3>
                        <button
                            onClick={() => {
                                const n = new Date();
                                n.setHours(0, 0, 0, 0);
                                setAnchor(n);
                                setSelected(todayStr());
                            }}
                            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-meye-cyan mt-0.5 font-mono"
                        >
                            jump to today
                        </button>
                    </div>
                    <button
                        onClick={() => setAnchor((a) => { const n = new Date(a); n.setDate(a.getDate() + 7); return n; })}
                        className="w-8 h-8 rounded-lg border border-meye-border bg-black/40 text-zinc-400 hover:text-white flex items-center justify-center"
                        data-testid="weekly-next"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
                <div className="grid grid-cols-7 gap-1 mt-3">
                    {days.map((d) => {
                        const iso = fmtISO(d);
                        const dayTasks = tasksByDate[iso] || [];
                        const recurringCount = dayTasks.filter((t) => t.type === "recurring" && t.interval).length;
                        const regularCount   = dayTasks.length - recurringCount;
                        const count = dayTasks.length;
                        const isToday = iso === today;
                        const isSelected = iso === selected;
                        const bg = isSelected
                            ? "border-violet-400/70 bg-violet-500/20"
                            : isToday
                                ? "border-meye-fuchsia/70 bg-meye-fuchsia/15 hover:bg-meye-fuchsia/25"
                                : count > 0
                                    ? "border-meye-cyan/60 bg-meye-cyan/15 hover:bg-meye-cyan/25"
                                    : "border-meye-border bg-black/30 hover:border-zinc-600";
                        const labelText = isToday ? "TODAY" : WEEKDAY_ABBR[d.getDay()];
                        return (
                            <button
                                type="button"
                                key={iso}
                                onClick={() => selectDay(iso)}
                                className={`aspect-square rounded-lg border flex flex-col items-center justify-center transition-colors ${bg} px-1`}
                                data-testid={`weekly-cell-${iso}`}
                                data-today={isToday}
                                title={count > 0 ? `${regularCount} regular · ${recurringCount} recurring` : ""}
                            >
                                <span className={`font-brand font-bold uppercase tracking-widest ${isToday ? "text-[10px]" : "text-[11px]"} ${isSelected || isToday || count > 0 ? "text-white" : "text-zinc-400"}`}>
                                    {labelText}
                                </span>
                                <span className={`font-brand font-bold text-sm mt-0.5 ${isSelected || isToday || count > 0 ? "text-white" : "text-zinc-400"}`}>
                                    {d.getDate()}
                                </span>
                                {/* Split badge — Calendar icon for regular one-shot tasks
                                    (amber), RotateCw for recurring cadences (cyan). Two visually
                                    distinct colours so a glance separates workload composition
                                    without reading the numbers. Sizes trimmed back so 7 cells fit
                                    on a phone width without wrapping. */}
                                {count > 0 && (
                                    <div className="flex items-center gap-1 mt-1" data-testid={`weekly-count-${iso}`}>
                                        {regularCount > 0 && (
                                            <span className={`flex items-center gap-0.5 text-sm font-brand font-bold ${
                                                isSelected ? "text-white" : "text-amber-400"
                                            }`}>
                                                <Calendar size={12} strokeWidth={2.25} />
                                                {regularCount}
                                            </span>
                                        )}
                                        {recurringCount > 0 && (
                                            <span className={`flex items-center gap-0.5 text-sm font-brand font-bold ${
                                                isSelected ? "text-white" : "text-cyan-400"
                                            }`}>
                                                <RotateCw size={12} strokeWidth={2.25} />
                                                {recurringCount}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Selected-day tasks. Matches the screensaver's "jump to that day" behavior — only
                the tapped day's items render below, not the whole week. */}
            <div className="mt-4" data-testid={`weekly-day-section-${selected}`}>
                <h4 className="text-[11px] font-brand font-bold uppercase tracking-widest text-zinc-300 mb-2 flex items-center gap-2">
                    <span>{selectedLabel}</span>
                    {selected === today && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded border border-meye-fuchsia/50 bg-meye-fuchsia/10 text-meye-fuchsia">
                            Today
                        </span>
                    )}
                    {selectedTasks.length > 0 && (
                        <span className="ml-auto text-[10px] font-mono text-meye-cyan" data-testid="weekly-selected-count">
                            {selectedTasks.length} {selectedTasks.length === 1 ? "task" : "tasks"}
                        </span>
                    )}
                </h4>
                {selectedTasks.length === 0 ? (
                    <div className="rounded border border-meye-border/50 bg-black/20 px-3 py-1.5 text-[11px] text-zinc-600 font-mono">
                        · nothing scheduled
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {selectedTasks.map((t) => (
                            <CompactTaskCard
                                key={`${t.id}:${t._occKey || ""}`}
                                task={t}
                                attachments={attachments}
                                occurrenceIso={t._occKey || selected}
                                onToggle={onToggle}
                                onEdit={(row) => onEdit({ ...row, _occurrenceDate: selected })}
                                searchTerm={searchTerm}
                                onPostpone={onPostpone}
                                onUndoPostpone={onUndoPostpone}
                                undoFromIso={t._movedFrom || null}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WeeklyView;
